/**
 * AssistantScreen — chat do Assistente com STREAMING real.
 *
 * Os tokens do Claude aparecem AOS POUCOS (SSE via POST /assistant/stream), em
 * vez de esperar a resposta inteira. Demonstra o caminho de streaming
 * ponta-a-ponta (StreamChat gRPC → SSE → browser).
 */
import { useRef, useState, useEffect } from 'react';
import { C } from '@/theme/palette';
import { useApi } from '@/api/client';
import { useProjects } from '@/api/projects';
import { streamAssistant } from '@/api/assistant';

const TIERS = [
  { id: 'haiku', label: 'Rápido (Haiku)' },
  { id: 'sonnet', label: 'Equilibrado (Sonnet)' },
];

function TypingDots() {
  // "pensando…": três pontos com bounce escalonado (pulso suave se reduced-motion).
  return (
    <span className="nf-typing" role="status" aria-label="Assistente pensando">
      <i /><i /><i />
    </span>
  );
}

function Bubble({ role, content, streaming, sources }) {
  const isUser = role === 'user';
  const isError = role === 'error';
  const thinking = streaming && !content;   // esperando o 1º token
  return (
    <div style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}>
      <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{
          padding: '10px 14px', borderRadius: 12, whiteSpace: 'pre-wrap',
          wordBreak: 'break-word', fontSize: 14, lineHeight: 1.5,
          background: isUser ? C.accent : isError ? 'transparent' : C.blackElev,
          color: isUser ? C.black : isError ? C.bad : C.text,
          border: isUser ? 'none' : `1px solid ${isError ? C.bad : C.blackBorder}`,
        }}>
          {thinking ? <TypingDots /> : content}
          {streaming && content ? <span style={{ opacity: 0.6 }}>▍</span> : null}
        </div>
        {sources && sources.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingLeft: 2 }}>
            <span style={{ fontSize: 11, color: C.neutralMuted, alignSelf: 'center' }}>fontes:</span>
            {sources.map((f, i) => (
              <span key={`${f}-${i}`} style={{
                fontSize: 11, fontFamily: 'ui-monospace, monospace', color: C.neutralDim,
                background: C.black, border: `1px solid ${C.blackBorder}`,
                borderRadius: 6, padding: '2px 7px',
              }}>{f}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function AssistantScreen() {
  const api = useApi();
  const { data: projects } = useProjects();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [tier, setTier] = useState('haiku');
  const [projectId, setProjectId] = useState('');
  const [busy, setBusy] = useState(false);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages]);

  useEffect(() => () => abortRef.current?.abort(), []);

  function send() {
    const text = input.trim();
    if (!text || busy) return;
    const userMsg = { role: 'user', content: text };
    const history = [...messages.filter((m) => m.role !== 'error'), userMsg];
    setMessages([...messages, userMsg, { role: 'assistant', content: '', streaming: true }]);
    setInput('');
    setBusy(true);

    const controller = new AbortController();
    abortRef.current = controller;

    const appendToLast = (delta) =>
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') next[next.length - 1] = { ...last, content: last.content + delta };
        return next;
      });
    const finish = (patch) =>
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') next[next.length - 1] = { ...last, streaming: false, ...patch };
        return next;
      });

    const attachSources = (files) =>
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') next[next.length - 1] = { ...last, sources: files };
        return next;
      });

    streamAssistant(api, history, {
      tier,
      projectId: projectId || undefined,
      signal: controller.signal,
      onSources: attachSources,
      onToken: appendToLast,
      onDone: () => { finish({}); setBusy(false); },
      onError: (msg) => {
        setMessages((prev) => [...prev, { role: 'error', content: `Falha: ${msg}` }]);
        finish({});
        setBusy(false);
      },
    });
  }

  function stop() {
    abortRef.current?.abort();
    setBusy(false);
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') next[next.length - 1] = { ...last, streaming: false };
      return next;
    });
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%', maxWidth: 860,
      margin: '0 auto', padding: '16px clamp(12px, 3vw, 28px)',
    }}>
      <style>{`
        .nf-typing { display: inline-flex; gap: 4px; align-items: center; height: 1.2em; }
        .nf-typing i {
          width: 6px; height: 6px; border-radius: 50%;
          background: currentColor; opacity: .35;
          animation: nf-bounce 1.2s infinite ease-in-out both;
        }
        .nf-typing i:nth-child(1) { animation-delay: -0.24s; }
        .nf-typing i:nth-child(2) { animation-delay: -0.12s; }
        @keyframes nf-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: .35; }
          40% { transform: translateY(-4px); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .nf-typing i { animation: nf-pulse 1.4s infinite ease-in-out; }
          @keyframes nf-pulse { 0%, 100% { opacity: .3; } 50% { opacity: 1; } }
        }
      `}</style>
      <div ref={scrollRef} style={{
        flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10,
        padding: '8px 2px 16px',
      }}>
        {messages.length === 0 && (
          <div style={{ color: C.neutralMuted, fontSize: 14, marginTop: 24, textAlign: 'center' }}>
            Pergunte algo ao assistente — a resposta do Claude aparece token a token.<br />
            {projectId
              ? 'Ancorado no projeto: código indexado + memória da jornada (decisões/gates) + grafo de dependências. As respostas citam as fontes.'
              : 'Escolha um projeto abaixo para o assistente responder com base no repo indexado, na memória da jornada e no grafo do código.'}
          </div>
        )}
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} content={m.content} streaming={m.streaming} sources={m.sources} />
        ))}
      </div>

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, paddingTop: 10,
        borderTop: `1px solid ${C.blackBorder}`,
      }}>
        <span style={{ fontSize: 12, color: C.neutralMuted }}>Sobre o projeto:</span>
        <select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          disabled={busy}
          aria-label="Projeto para ancorar o RAG"
          style={{
            background: C.blackElev, color: C.text, border: `1px solid ${C.blackBorder}`,
            borderRadius: 10, padding: '6px 10px', fontSize: 13, maxWidth: 320,
          }}
        >
          <option value="">Nenhum (chat geral)</option>
          {(Array.isArray(projects) ? projects : []).map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {projectId && (
          <span style={{ fontSize: 11, color: C.accent }}>● ancorado no repo indexado</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', paddingTop: 8 }}>
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          disabled={busy}
          aria-label="Modelo"
          style={{
            background: C.blackElev, color: C.text, border: `1px solid ${C.blackBorder}`,
            borderRadius: 10, padding: '10px 10px', fontSize: 13,
          }}
        >
          {TIERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Escreva uma mensagem… (Enter envia, Shift+Enter quebra linha)"
          rows={1}
          style={{
            flex: 1, resize: 'none', background: C.blackElev, color: C.text,
            border: `1px solid ${C.blackBorder}`, borderRadius: 10, padding: '10px 12px',
            fontSize: 14, fontFamily: 'inherit', maxHeight: 140,
          }}
        />
        {busy ? (
          <button onClick={stop} style={btnStyle(C.bad, C.text)}>Parar</button>
        ) : (
          <button onClick={send} disabled={!input.trim()} style={btnStyle(C.accent, C.black, !input.trim())}>
            Enviar
          </button>
        )}
      </div>
    </div>
  );
}

function btnStyle(bg, fg, disabled = false) {
  return {
    background: disabled ? C.blackElev2 : bg, color: disabled ? C.neutralMuted : fg,
    border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer', whiteSpace: 'nowrap',
  };
}

export default AssistantScreen;
